import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Checkbox,
  Chip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  InsertDriveFile as FileIcon,
  Description as DocumentIcon,
  Code as CodeIcon,
  Article as ArticleIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import AionaIcon from '../../assets/aiona/AIONA_ファビコン.png';

export interface SearchResult {
  id: string;
  title: string;
  path: string[];
  type: 'document' | 'code' | 'design' | 'article';
  snippet?: string;
  excerpt?: string;
  relevance?: number;
  relevanceLabel?: 'high' | 'medium' | 'low';
  rawRelevance?: number;
  relevanceSource?: string;
  citationIndex?: number;
  selected?: boolean;
  pageNumber?: number;
  segmentId?: string;
}

interface SearchResultsCardProps {
  query: string;
  results: SearchResult[];
  onResultClick?: (result: SearchResult, query: string) => void;
  onAddToContext?: (results: SearchResult[]) => Promise<void>;
  selectedFileIds?: string[];
}

// WCAG 2.1 AA: filled chips with sufficient contrast, text labels not color-only
const RELEVANCE_CHIP_SX = { height: 20, fontSize: 10, fontWeight: 600, color: '#fff' };
type RelevanceLabel = 'high' | 'medium' | 'low';
interface RelevanceBadgeLabels {
  prefix: string;
  high: string;
  medium: string;
  low: string;
}

const DEFAULT_RELEVANCE_LABELS: RelevanceBadgeLabels = {
  prefix: 'Relevance',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

const resolveRelevanceLabel = (
  score?: number,
  calibratedLabel?: RelevanceLabel,
): RelevanceLabel | undefined => {
  if (calibratedLabel) return calibratedLabel;
  if (score == null) return undefined;
  if (score >= 0.7) return 'high';
  if (score >= 0.4) return 'medium';
  return 'low';
};

export const getRelevanceBadgeLabel = (
  score?: number,
  calibratedLabel?: RelevanceLabel,
  labels: RelevanceBadgeLabels = DEFAULT_RELEVANCE_LABELS,
) => {
  const label = resolveRelevanceLabel(score, calibratedLabel);
  if (!label) return null;
  return `${labels.prefix}: ${labels[label]}`;
};

const getRelevanceBadge = (
  score: number | undefined,
  calibratedLabel: RelevanceLabel | undefined,
  labels: RelevanceBadgeLabels,
) => {
  const label = resolveRelevanceLabel(score, calibratedLabel);
  const text = getRelevanceBadgeLabel(score, calibratedLabel, labels);
  if (!label || !text) return null;
  if (label === 'high') return <Chip label={text} size="small" color="success" variant="filled" sx={RELEVANCE_CHIP_SX} />;
  if (label === 'medium') return <Chip label={text} size="small" color="warning" variant="filled" sx={RELEVANCE_CHIP_SX} />;
  return <Chip label={text} size="small" variant="filled" sx={{ ...RELEVANCE_CHIP_SX, bgcolor: 'grey.500' }} />;
};

// Group multiple ``SearchResult`` rows that share the same ``id``
// (= document file id) into one card so the chat panel doesn't show
// the same file name five times when the retriever surfaced five
// segments from it. ``per_document_limit > 1`` (PR #677) and the
// segment neighbor fetch (PR #680) intentionally emit per-segment
// rows so the LLM gets richer evidence; this grouping is purely a
// display concern (Issue #672 follow-up after 2026-05-18 demo).
//
// The highest-relevance segment per file becomes the visible row;
// remaining segments expand under a localized toggle so citation
// indices the LLM emits ([3], [4], [5]) all stay resolvable to a
// clickable list item.
export interface GroupedResult {
  fileId: string;
  primary: SearchResult;
  additional: SearchResult[]; // [] when only one segment matched
}

// Codex P2 round 5 on PR #682: when ``id`` is missing the
// previous fallback was just ``r.title``, which collapses two
// different files sharing a filename (e.g. ``README.md`` in
// different folders) into one card and routes clicks to the
// wrong source. Fold ``path`` into the fallback so the key is
// stable per logical document but distinct across folders.
function fileKey(r: SearchResult): string {
  if (r.id) return r.id;
  const pathPart = (r.path ?? []).join('/');
  return pathPart ? `${pathPart}/${r.title ?? ''}` : (r.title ?? '');
}

export function groupResultsByFile(results: SearchResult[]): GroupedResult[] {
  const groups = new Map<string, SearchResult[]>();
  for (const r of results) {
    const key = fileKey(r);
    const existing = groups.get(key);
    if (existing) {
      existing.push(r);
    } else {
      groups.set(key, [r]);
    }
  }
  const grouped: GroupedResult[] = [];
  for (const [fileId, segments] of groups.entries()) {
    // Highest-relevance segment wins the primary slot so the
    // collapsed card shows the strongest evidence first.
    const sorted = [...segments].sort(
      (a, b) => (b.relevance ?? 0) - (a.relevance ?? 0),
    );
    grouped.push({
      fileId,
      primary: sorted[0],
      additional: sorted.slice(1),
    });
  }
  // Preserve the original ordering of the first occurrence of each
  // file so the visible row sequence matches the LLM's confidence
  // ordering across files (top file first).
  const firstSeen = new Map<string, number>();
  results.forEach((r, idx) => {
    const key = fileKey(r);
    if (!firstSeen.has(key)) firstSeen.set(key, idx);
  });
  grouped.sort(
    (a, b) => (firstSeen.get(a.fileId) ?? 0) - (firstSeen.get(b.fileId) ?? 0),
  );
  return grouped;
}

const SearchResultsCard: React.FC<SearchResultsCardProps> = ({
  query,
  results,
  onResultClick,
  onAddToContext,
  selectedFileIds = [],
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const [selectedResults, setSelectedResults] = useState<Set<string>>(new Set());
  // Per-file "show additional matched passages" toggle. Keyed by
  // ``fileId`` so toggling one card doesn't expand sibling cards.
  const [openSegments, setOpenSegments] = useState<Set<string>>(new Set());
  const groupedResults = React.useMemo(() => groupResultsByFile(results), [results]);
  const relevanceLabels = React.useMemo<RelevanceBadgeLabels>(() => ({
    prefix: t('chat.relevance'),
    high: t('chat.relevanceHigh'),
    medium: t('chat.relevanceMedium'),
    low: t('chat.relevanceLow'),
  }), [t]);

  // Fallback citation numbers for callers that don't populate
  // ``citationIndex`` (legacy SSE mapping in ``chatService``,
  // tests, etc.). Numbering matches the visible row order:
  // primary first, then expander rows by group, mirroring the
  // pre-grouping behaviour ``[{result.citationIndex ?? index + 1}]``
  // (Codex P2 review on PR #682). Built off the grouped order
  // so the badges stay stable when a user expands a card.
  const fallbackIndex = React.useMemo(() => {
    const map = new Map<SearchResult, number>();
    let counter = 0;
    for (const group of groupedResults) {
      counter += 1;
      map.set(group.primary, counter);
      for (const seg of group.additional) {
        counter += 1;
        map.set(seg, counter);
      }
    }
    return map;
  }, [groupedResults]);
  const badgeNumber = (result: SearchResult) =>
    result.citationIndex ?? fallbackIndex.get(result) ?? 1;

  // Codex P2 round 6 on PR #682: the round-5 fix split id-less
  // sources into separate groups by ``fileKey``, but selection
  // and "already in context" checks were still keyed by
  // ``group.primary.id``. When ``id`` is empty (the case
  // ``fileKey`` exists to handle) every id-less group collapsed
  // to the same key ``''`` in the local Set, so checking one
  // card marked all id-less cards selected and routed the
  // add-to-context callback through whichever raw result
  // ``find`` returned first.
  //
  // Switch the selection state to ``fileKey``-based, and operate
  // on the resolved group so the primary result reaches
  // ``onAddToContext`` unambiguously. ``selectedFileIds`` from
  // the parent stays id-based; id-less rows can never appear
  // there, so ``isAlreadyInContext`` short-circuits to false
  // when the primary id is empty.
  const handleToggleSelectGroup = async (group: GroupedResult) => {
    const fileKeyId = group.fileId;
    const result = group.primary;
    const isCurrentlySelected = selectedResults.has(fileKeyId);

    if (!isCurrentlySelected && onAddToContext) {
      console.log('Auto-adding to context:', result);
      try {
        await onAddToContext([result]);
        console.log('File added to context successfully');
      } catch (error) {
        console.error('Error adding file to context:', error);
      }
    }

    setSelectedResults(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fileKeyId)) {
        newSet.delete(fileKeyId);
      } else {
        newSet.add(fileKeyId);
      }
      return newSet;
    });
  };


  const isGroupAlreadyInContext = (group: GroupedResult) => {
    const id = group.primary.id;
    if (!id) return false;
    return selectedFileIds.includes(id);
  };

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'code':
        return <CodeIcon sx={{ fontSize: 18, color: 'rgba(71, 70, 68, 0.6)' }} />;
      case 'design':
        return <ArticleIcon sx={{ fontSize: 18, color: 'rgba(71, 70, 68, 0.6)' }} />;
      case 'document':
        return <DocumentIcon sx={{ fontSize: 18, color: 'rgba(71, 70, 68, 0.6)' }} />;
      default:
        return <FileIcon sx={{ fontSize: 18, color: 'rgba(71, 70, 68, 0.6)' }} />;
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '10px',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'rgba(84, 72, 49, 0.08)',
        boxShadow: 'rgba(0, 0, 0, 0.04) 0px 1px 2px',
        backgroundColor: '#fff',
        transition: 'box-shadow 0.15s ease-in-out',
        '&:hover': {
          boxShadow: 'rgba(0, 0, 0, 0.08) 0px 2px 4px',
        },
      }}
    >
      {/* Header with search summary */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.75,
          pt: 1.5,
          pb: 0.5,
        }}
      >
        <IconButton
          size="small"
          onClick={() => setExpanded(!expanded)}
          sx={{ p: 0.25 }}
        >
          <SearchIcon sx={{ fontSize: 16, color: 'rgba(71, 70, 68, 0.6)' }} />
        </IconButton>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: 14,
              color: 'rgb(115, 114, 110)',
              flexShrink: 0,
            }}
          >
            {t('chat.searchResultsCount', { count: results.length })}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 14,
                height: 14,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {/* AIONAアイコンを表示 */}
              <img
                src={AionaIcon}
                alt="AIONA"
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: '3px',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />

            </Box>
            <Typography
              variant="caption"
              sx={{
                fontSize: 12,
                color: 'rgb(115, 114, 110)',
              }}
            >
              AIONA DB
            </Typography>
          </Box>
        </Box>

        <IconButton
          size="small"
          sx={{
            width: 20,
            height: 20,
            transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.15s ease-in-out',
            opacity: 0.5,
          }}
        >
          <ExpandMoreIcon sx={{ fontSize: 16, color: 'rgba(71, 70, 68, 0.6)' }} />
        </IconButton>
      </Box>

      {/* Search query display with Add Context button */}
      <Box
        sx={{
          px: 1.75,
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
          <Typography
            variant="caption"
            sx={{
              fontSize: 14,
              color: 'rgb(115, 114, 110)',
            }}
          >
            {t('chat.searchedFor')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: 14,
              color: 'rgb(50, 48, 44)',
              fontWeight: 400,
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            "{query}"
          </Typography>
        </Box>
      </Box>

      {/* Results list */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box
          sx={{
            maxHeight: 200,
            overflow: 'auto',
            borderTop: '1px solid rgba(84, 72, 49, 0.08)',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              borderRadius: '3px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.15)',
              },
            },
          }}
        >
          <List dense sx={{ p: 0.5 }}>
            {groupedResults.map((group) => {
              const isSelected = selectedResults.has(group.fileId);
              const isInContext = isGroupAlreadyInContext(group);
              const segmentCount = 1 + group.additional.length;
              const showAdditional = openSegments.has(group.fileId);

              const renderPassageRow = (
                result: SearchResult,
                opts: { isPrimary: boolean; index: number },
              ) => (
                // Codex P2 round 4 on PR #682: pre-grouping every
                // source row was a ``ListItemButton`` so users could
                // click any citation to open the backing document.
                // The expanded ``additional`` rows must keep that
                // behavior — otherwise [4]/[5] in the LLM answer are
                // shown but unclickable. Reuse ``ListItemButton``
                // (button role + keyboard activation) and route the
                // click to the segment itself.
                <ListItemButton
                  // Codex P2 round 3: legacy callers can omit
                  // ``citationIndex`` AND share the same ``id`` across
                  // grouped segments (which is the whole point of
                  // grouping). ``${fileId}-${id}`` then collides for
                  // every additional row and React drops all but one.
                  // Include the position in the additional array so
                  // each row gets a unique key without depending on
                  // caller-supplied ids.
                  key={`${group.fileId}-${result.citationIndex ?? `seg-${opts.index}`}`}
                  onClick={() => onResultClick?.(result, query)}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 0.5,
                    px: opts.isPrimary ? 0 : 4.5,
                    py: 0.25,
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: 'rgba(55, 53, 47, 0.06)',
                    },
                  }}
                >
                  {/* Citation number badge — present on every passage row
                      so [3]/[4]/[5] in the LLM answer all resolve here. */}
                  <Box
                    sx={{
                      minWidth: 24,
                      height: 20,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(25, 118, 210, 0.12)',
                      borderRadius: '4px',
                      mt: 0.25,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: 'rgb(25, 118, 210)',
                      }}
                    >
                      [{badgeNumber(result)}]
                    </Typography>
                  </Box>
                  {result.snippet && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: 12,
                        color: 'rgba(50, 48, 44, 0.78)',
                        flex: 1,
                        lineHeight: 1.45,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {result.snippet.length > 220
                        ? result.snippet.slice(0, 220) + '...'
                        : result.snippet}
                    </Typography>
                  )}
                </ListItemButton>
              );

              return (
                <Box key={group.fileId} sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={(e) => {
                      // Only trigger onResultClick if not clicking on checkbox
                      const target = e.target as HTMLElement;
                      if (!target.closest('.MuiCheckbox-root')) {
                        onResultClick?.(group.primary, query);
                      }
                    }}
                    sx={{
                      borderRadius: '6px',
                      px: 1,
                      py: 0.5,
                      minHeight: 28,
                      backgroundColor: isSelected ? 'rgba(76, 175, 80, 0.08)' : 'transparent',
                      '&:hover': {
                        backgroundColor: isSelected ? 'rgba(76, 175, 80, 0.12)' : 'rgba(55, 53, 47, 0.08)',
                      },
                    }}
                  >
                    {/* Citation number badge — top-relevance segment of this file. */}
                    <Box
                      sx={{
                        minWidth: 24,
                        height: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(25, 118, 210, 0.12)',
                        borderRadius: '4px',
                        mr: 1,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: 'rgb(25, 118, 210)',
                        }}
                      >
                        [{badgeNumber(group.primary)}]
                      </Typography>
                    </Box>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Checkbox
                        checked={isSelected}
                        disabled={isInContext}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isInContext) {
                            handleToggleSelectGroup(group);
                          }
                        }}
                        size="small"
                        sx={{ p: 0 }}
                      />
                    </ListItemIcon>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      {getFileIcon(group.primary.type)}
                    </ListItemIcon>
                    <ListItemText
                      primaryTypographyProps={{ component: 'div' }}
                      secondaryTypographyProps={{ component: 'div' }}
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: 14,
                              color: 'rgb(50, 48, 44)',
                              fontWeight: 400,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              flex: 1,
                            }}
                          >
                            {group.primary.title}
                          </Typography>
                          {segmentCount > 1 && (
                            <Chip
                              label={t('chat.relatedPassages', { count: segmentCount })}
                              size="small"
                              variant="outlined"
                              sx={{
                                height: 20,
                                fontSize: 10,
                                fontWeight: 500,
                                borderColor: 'rgba(25, 118, 210, 0.4)',
                                color: 'rgb(25, 118, 210)',
                              }}
                            />
                          )}
                          {getRelevanceBadge(group.primary.relevance, group.primary.relevanceLabel, relevanceLabels)}
                          {isInContext && (
                            <Chip
                              icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                              label={t('chat.inContext')}
                              size="small"
                              color="success"
                              sx={{
                                height: 20,
                                fontSize: 11,
                                '& .MuiChip-icon': {
                                  fontSize: 14,
                                },
                              }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          {group.primary.snippet && (
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: 12,
                                color: 'rgba(50, 48, 44, 0.7)',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                lineHeight: 1.4,
                                mb: 0.25,
                              }}
                            >
                              {group.primary.snippet.length > 120
                                ? group.primary.snippet.slice(0, 120) + '...'
                                : group.primary.snippet}
                            </Typography>
                          )}
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: 12,
                              color: 'rgba(70, 68, 64, 0.45)',
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            {group.primary.path.join(' / ')}
                          </Typography>
                        </Box>
                      }
                      sx={{ my: 0 }}
                    />
                  </ListItemButton>

                  {/* Additional matched passages from the same file —
                      collapsed by default. Each carries its own citation
                      index so the LLM's [n] anchors still land somewhere
                      clickable when the user expands the group. */}
                  {group.additional.length > 0 && (
                    <Box sx={{ pl: 4.5, pr: 1, pt: 0.25 }}>
                      <Typography
                        component="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenSegments((prev) => {
                            const next = new Set(prev);
                            if (next.has(group.fileId)) next.delete(group.fileId);
                            else next.add(group.fileId);
                            return next;
                          });
                        }}
                        sx={{
                          background: 'none',
                          border: 'none',
                          p: 0,
                          fontSize: 11,
                          color: 'rgb(25, 118, 210)',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          textUnderlineOffset: 2,
                        }}
                      >
                        {showAdditional
                          ? t('chat.hideRelatedPassages')
                          : t('chat.showRelatedPassages', {
                              count: group.additional.length,
                              unit: group.additional.length === 1
                                ? t('chat.relatedPassage')
                                : t('chat.relatedPassagesUnit'),
                            })}
                      </Typography>
                      <Collapse in={showAdditional} timeout="auto" unmountOnExit>
                        <Box sx={{ pt: 0.5 }}>
                          {group.additional.map((seg, segIdx) =>
                            renderPassageRow(seg, { isPrimary: false, index: segIdx }),
                          )}
                        </Box>
                      </Collapse>
                    </Box>
                  )}
                </Box>
              );
            })}
          </List>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default SearchResultsCard;
